import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';

import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import DateFormatter from '../../../../../components/utils/DateFormatter';
import NameAvatar from '../../../../../components/avatars/Avatar';
import Button from '../../../../../components/buttons/Button';
import NA from '../../../../../components/utils/NA';

import { 
  GetSmartLCBIZcore, 
  UpdateJobSplitStatusBIZcore, 
  UpdateSmartLCAttachmentStatusBIZcore
} from '../../../../../redux/actions/business/core/smartLCsActions';

import * as actionTypes from '../../../../../redux/actionTypes';
import LoadingButton from '../../../../../components/buttons/Button';

const SmartLCsApprovalsModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const { handleShowAmount } = useCurrencyService();
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [splitActionLoading, setSplitActionLoading] = useState(false);
  const [attachmentActionLoading, setAttachmentActionLoading] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setLoading(true);
    setJobDetails(null);
    setSelectedAttachment(null);
    setAttachmentModalOpen(false);
    dispatch({ type: actionTypes.CLEAR_SMART_LC_BIZ_CORE });
  };

  useEffect(() => {
    try {
      if(open){
        if(update?.defaultMode === 1){
          // mode 1 - view only
          handleGetJobDetails();
        }else{
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    try {
      if(state.smartLCDetails){
        setJobDetails(state.smartLCDetails);
      }
    } catch (err) { }
  }, [state]);

  const handleGetJobDetails = async (customLoading) => {
    await dispatch(GetSmartLCBIZcore(update.id, customLoading ? customLoading : setLoading));
  };

  const handleViewAttachment = (attachment) => {
    setSelectedAttachment(attachment);
    setAttachmentModalOpen(true);
  };

  const handleValidateAttachment = async (attachment) => {
    try {
      let obj = {
        "validation_status": "VALIDATED"
      }

      await dispatch(UpdateSmartLCAttachmentStatusBIZcore(attachment.id, obj, setAttachmentActionLoading));
      await handleGetJobDetails(setAttachmentActionLoading);
      setAttachmentModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectAttachment = async (attachment) => {
    try {
      let obj = {
        "validation_status": "REJECTED"
      }

      await dispatch(UpdateSmartLCAttachmentStatusBIZcore(attachment.id, obj, setAttachmentActionLoading));
      await handleGetJobDetails(setAttachmentActionLoading);
      setAttachmentModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleValidateSplit = async (split) => {
    try {
      let obj = {
        "status": "VALIDATED"
      }
      await dispatch(UpdateJobSplitStatusBIZcore(split.id, obj, setSplitActionLoading));
      await handleGetJobDetails(setSplitActionLoading);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectSplit = async (split) => {
    try {
      let obj = {
        "status": "FAILED_VALIDATION"
      }
      await dispatch(UpdateJobSplitStatusBIZcore(split.id, obj, setSplitActionLoading));
      await handleGetJobDetails(setSplitActionLoading);
    } catch (err) {
      console.error(err);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return <ImageIcon />;
    if (fileType?.includes('pdf')) return <PictureAsPdfIcon />;
    return <DescriptionIcon />;
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return 'Unknown file';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'Unknown file';
  };

  const getValidationStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'VALIDATED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING_VALIDATION':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSplitStatusColor = (status) => {
    switch (status) {
      case 'LOCKED':
        return 'warning';
      case 'VALIDATED':
        return 'success';
      case 'FAILED_VALIDATION':
        return 'error';
      case 'RELEASED':
        return 'success';
      case 'CANCELLED':
        return 'default';
      case 'FAILED':
        return 'error';
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="smart-lc-modal-title"
        aria-describedby="smart-lc-modal-description"
      >
        <Box 
          className="modal--wide animate__animated animate__fadeIn"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '85%', md: '80%', lg: '1200px' },
            maxHeight: '90vh',
            overflowY: 'auto',
            p: 3,
          }}
        >
          {/* Header */}
          <Box className="flexCenterSBRow" mb={3}>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {loading ? (
            <Box className="flexCenterCenterRow" py={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Company Details */}
              <Box 
                className="block"
                sx={{ p: 2, mb: 2 }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  Company Details
                </Typography>
                <Box className="flexCenter_Row" gap={2}>
                  <NameAvatar color={jobDetails?.company?.color || null}>
                    {jobDetails?.company?.company_name || "NA"}
                  </NameAvatar>
                  <Box>
                    <Typography fontWeight={600}>
                      {jobDetails?.company?.company_name || <NA />}
                    </Typography>
                    <Typography variant="caption" color="grey">
                      Company ID: {jobDetails?.company?.company_id || <NA />}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Basic Details */}
              <Box 
                className="block"
                sx={{ p: 2, mb: 2 }}
              >
                <Typography variant="subtitle2" fontWeight={600} mb={2}>
                  LC Details
                </Typography>
                <Box className="grid_2items" gap={2}>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Reference
                    </Typography>
                    <Typography fontWeight={600}>
                      {jobDetails?.entity_reference || <NA />}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Status
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        size="small"
                        label={jobDetails?.status || "NA"}
                        color={jobDetails?.status === "ACTIVE_LOCKED" ? "success" : "default"}
                        variant={jobDetails?.status === "ACTIVE_LOCKED" ? "filled" : "outlined"}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Vendor
                    </Typography>
                    <Box className="flexCenter_Row" gap={1} mt={0.5}>
                      <NameAvatar 
                        size="small"
                        color={jobDetails?.vendor?.color || null}>
                        {jobDetails?.vendor?.name || "NA"}
                      </NameAvatar>
                      <Typography>
                        {jobDetails?.vendor?.name || <NA />}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Total Amount
                    </Typography>
                    <Typography fontWeight={700} fontSize="18px" color="primary">
                      {handleShowAmount(jobDetails?.base_currency, jobDetails?.total_base_amount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Expiry Date
                    </Typography>
                    <Typography>
                      {jobDetails?.expiry_date ? DateFormatter(jobDetails.expiry_date, "UTC") : <NA />}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Job Type
                    </Typography>
                    <Typography>
                      {jobDetails?.job_type || <NA />}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Created At
                    </Typography>
                    <Typography>
                      {jobDetails?.created_at ? DateFormatter(jobDetails.created_at, "UTC") : <NA />}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="grey">
                      Release Trigger
                    </Typography>
                    <Typography>
                      {jobDetails?.metadata?.release_trigger || <NA />}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Job Splits Table */}
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Job Splits
                </Typography>
                <Table className="block">
                  <TableHead>
                    <TableRow>
                      <TableCell>Split ID</TableCell>
                      <TableCell>Beneficiary</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payout Speed</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Release Condition</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!jobDetails?.splits || jobDetails.splits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="grey">No splits found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      jobDetails.splits.map((split, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: "monospace",
                                fontSize: "11px"
                              }}>
                              {split.id ? split.id.substring(0, 12) + "..." : "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography>
                              {split.beneficiary_name || jobDetails?.vendor?.name || <NA />}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600}>
                              {handleShowAmount(split.base_currency, split.base_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {split.payout_speed_mode || <NA />}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={split.status || "N/A"}
                              color={getSplitStatusColor(split.status)}
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {split.release_condition || <NA />}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box className="flexCenter_Row" gap={1}>
                              <LoadingButton
                                variant="outlined"
                                color="error"
                                size="small"
                                loading={splitActionLoading}
                                onClick={() => handleRejectSplit(split)}
                              >
                                Reject
                              </LoadingButton>
                              <LoadingButton
                                variant="contained"
                                color="success"
                                size="small"
                                loading={splitActionLoading}
                                onClick={() => handleValidateSplit(split)}
                              >
                                Validate
                              </LoadingButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Attachments Section */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Attachments
                </Typography>
                <Box className="block" sx={{ p: 2 }}>
                  {!jobDetails?.attachments || jobDetails.attachments.length === 0 ? (
                    <Typography color="grey" textAlign="center" py={2}>
                      No attachments found
                    </Typography>
                  ) : (
                    <Box className="flex__Column" gap={1}>
                      {jobDetails.attachments.map((attachment, index) => (
                        <Box
                          key={index}
                          className="flexCenterSBRow"
                          sx={{
                            p: 1.5,
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "8px",
                            backgroundColor: "rgba(44, 45, 47, 0.3)",
                          }}
                        >
                          <Box className="flexCenter_Row" gap={1.5}>
                            {getFileIcon(attachment.file_type)}
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {getFileNameFromUrl(attachment.file_url)}
                              </Typography>
                              <Box className="flexCenter_Row" gap={1} mt={0.5}>
                                <Typography variant="caption" color="grey">
                                  {attachment.file_type || "Unknown type"}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={attachment.validation_status || "UNKNOWN"}
                                  color={getValidationStatusColor(attachment.validation_status)}
                                  sx={{ height: '18px', fontSize: '10px' }}
                                />
                              </Box>
                            </Box>
                          </Box>
                          <Box className="flexCenter_Row" gap={1}>
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => handleViewAttachment(attachment)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                component="a"
                                href={attachment.file_url}
                                download
                                target="_blank"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box className="flexCenterFERow" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Attachment Viewer Modal */}
      <Modal
        open={attachmentModalOpen}
        onClose={() => setAttachmentModalOpen(false)}
        aria-labelledby="attachment-modal-title"
      >
        <Box
          className="modal animate__animated animate__fadeIn"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '70%' },
            maxHeight: '90vh',
            overflowY: 'auto',
            p: 3,
          }}
        >
          <Box className="flexCenterSBRow" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              {selectedAttachment?.file_name || "Attachment Viewer"}
            </Typography>
            <IconButton onClick={() => setAttachmentModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* File Preview */}
          <Box
            sx={{
              width: '100%',
              minHeight: '400px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {selectedAttachment?.file_type?.includes('image') ? (
              <img
                src={selectedAttachment.file_url}
                alt={selectedAttachment.file_name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                }}
              />
            ) : selectedAttachment?.file_type?.includes('pdf') ? (
              <iframe
                src={selectedAttachment.file_url}
                title={selectedAttachment.file_name}
                style={{
                  width: '100%',
                  height: '500px',
                  border: 'none',
                }}
              />
            ) : (
              <Box textAlign="center" p={4}>
                <DescriptionIcon sx={{ fontSize: 64, color: 'grey', mb: 2 }} />
                <Typography color="grey">
                  Preview not available for this file type
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  component="a"
                  href={selectedAttachment?.file_url}
                  target="_blank"
                >
                  Open in New Tab
                </Button>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box className="flexCenterSBRow" gap={2}>
            <Box className="flexCenter_Row" gap={1}>
              <Typography variant="body2" color="grey">
                Review and approve or reject this attachment
              </Typography>
            </Box>
            <Box className="flexCenter_Row" gap={2}>
              <LoadingButton
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                loading={attachmentActionLoading}
                onClick={() => handleRejectAttachment(selectedAttachment)}
              >
                Reject
              </LoadingButton>
              <LoadingButton
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                loading={attachmentActionLoading}
                onClick={() => handleValidateAttachment(selectedAttachment)}
              >
                Validate
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default SmartLCsApprovalsModal;